import { array, nonEmptyArray } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { NonEmptyArray } from 'fp-ts/NonEmptyArray';
import { Optional } from '@mews/std';
import { EnforceKeyableRecord, EnforceKeyableValue } from './types';

interface KeyByCurried<T> {
    <D extends T>(data: NonEmptyArray<D>): EnforceKeyableRecord<D, keyof T>;
    <D extends T>(data: Optional<Array<D>>): EnforceKeyableRecord<D, keyof T> | {};
}

export function keyBy<T, K extends keyof T>(key: K, data: NonEmptyArray<T>): EnforceKeyableRecord<T, K>;
export function keyBy<T, K extends keyof T>(key: K, data: Optional<Array<T>>): EnforceKeyableRecord<T, K> | {};
export function keyBy<T>(key: keyof T): KeyByCurried<T>;

export function keyBy<T>(...args: [keyof T, NonEmptyArray<T> | Optional<Array<T>>] | [keyof T]) {
    if (args.length === 2) {
        const [key, data] = args;
        return data && array.isNonEmpty(data)
            ? pipe(
                  data,
                  nonEmptyArray.reduce({} as EnforceKeyableRecord<T, keyof T>, (record, item) => {
                      const value = item[key] as EnforceKeyableValue<T, keyof T>;
                      record[value] = item;
                      return record;
                  })
              )
            : {};
    }
    const key = args[0];
    return <D extends T>(pData: NonEmptyArray<D> | Optional<Array<D>>) => keyBy<D, keyof T>(key, pData);
}
